/** Фото после сжатия: JPEG и его размер в пикселях. */
export type CompressedImage = { blob: Blob; width: number; height: number };

export type CompressOptions = {
  /** Длинная сторона результата не больше этого числа пикселей; меньшее фото не увеличивается. */
  maxSide: number;
  /** Качество JPEG от 0 до 1. */
  quality: number;
};

/**
 * Обработка изображения: декодирует фото (JPEG, PNG, HEIC — что умеет браузер), уменьшает и кодирует в JPEG.
 * Ошибка — браузер не смог прочитать файл. В тестах подменяется фейком.
 */
export type ImageCompressor = {
  compress: (file: Blob, options: CompressOptions) => Promise<CompressedImage>;
};

/** Размер, вписанный в квадрат `maxSide` с сохранением пропорций. */
export function scaledSize(width: number, height: number, maxSide: number): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** Декодирование через `createImageBitmap`, кодирование в JPEG через canvas. EXIF в результат не попадает. */
export function createImageCompressor(): ImageCompressor {
  return {
    async compress(file, { maxSide, quality }) {
      // Поворот из EXIF применяется при декодировании: иначе фото с телефона легло бы набок.
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      try {
        const { width, height } = scaledSize(bitmap.width, bitmap.height, maxSide);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Canvas 2D is unavailable");
        // У JPEG нет прозрачности: без белой подложки прозрачный PNG стал бы чёрным.
        context.fillStyle = "#fff";
        context.fillRect(0, 0, width, height);
        context.drawImage(bitmap, 0, 0, width, height);
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
        if (!blob) throw new Error("JPEG encoding failed");
        return { blob, width, height };
      } finally {
        bitmap.close();
      }
    },
  };
}
